import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown, Phone, Mail } from "lucide-react";
import ContactModal from "./ContactModal";
import { useReferral } from "@/contexts/ReferralContext";
import { useReducedMotion } from "@/hooks/useReducedMotion";

// Use public path for hero image to allow preloading before JS bundle parses
const heroImage = "/images/hero-consulting.webp";

const Hero = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { triggerContactWithReferral } = useReferral();
  const prefersReducedMotion = useReducedMotion();

  // Start with opacity: 1 for faster FCP - only animate subtle y movement
  const animationProps = prefersReducedMotion 
    ? {} 
    : { 
        initial: { opacity: 1, y: 10 } as const, 
        animate: { opacity: 1, y: 0 } as const, 
        transition: { duration: 0.4, ease: "easeOut" as const } 
      };

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    triggerContactWithReferral(() => {
      window.location.href = "tel:+4593889029";
    });
  };

  const scrollToContact = () => {
    const contactSection = document.querySelector('#contact-section');
    contactSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  
  const scrollToContent = () => {
    const nextSection = document.querySelector('#audience-section');
    nextSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img src={heroImage} alt="CECCA rådgivningsteam i møde med kunde om skadesrådgivning og forsikringsopgørelser" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/85 to-foreground/60 sm:to-foreground/50" />
        {/* Top gradient for header legibility */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 via-black/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-32">
        <motion.div 
          className="max-w-3xl"
          {...animationProps}
        >
          {/* Main Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-background mb-4 sm:mb-6 leading-tight">
            Vi står på din side,<br />
            <span className="text-accent">når skaden er sket</span>
          </h1>

          {/* Short description */}
          <p className="text-base sm:text-lg md:text-xl text-background/90 mb-6 sm:mb-8 max-w-2xl">
            Kontakt os i dag – første møde er gratis og uforpligtende.
          </p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Button 
              size="lg" 
              onClick={handlePhoneClick}
              aria-label="Kontakt os nu på telefon"
              className="h-12 sm:h-14 text-base bg-accent hover:bg-accent/90 text-accent-foreground font-semibold group"
            >
              <Phone className="mr-2 h-5 w-5" aria-hidden="true" />
              Kontakt os nu
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild
              className="h-12 sm:h-14 text-base bg-background/10 backdrop-blur-sm border-background/50 text-background hover:bg-background/20 hover:border-background font-semibold group"
            >
              <a href="mailto:cp@cecca.dk">
                Få et gratis møde
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </motion.div>
          
          {/* Contact Info */}
          <motion.div 
            className="pt-6 sm:pt-8 border-t border-background/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-10">
              <a href="tel:+4593889029" className="group" aria-label="Ring direkte til +45 93 88 90 29">
                <p className="text-xs sm:text-sm text-background/70 mb-1 flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                  Ring direkte:
                </p>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-background group-hover:text-accent transition-colors">
                  +45 93 88 90 29
                </span>
              </a>
              <a href="mailto:cp@cecca.dk" className="group" aria-label="Send email til cp@cecca.dk">
                <p className="text-xs sm:text-sm text-background/70 mb-1 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                  Skriv til os:
                </p>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-background group-hover:text-accent transition-colors">
                  cp@cecca.dk
                </span>
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator - hidden on mobile */}
      <motion.button 
        onClick={scrollToContent} 
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 text-background/80 hover:text-background transition-colors cursor-pointer hidden sm:block" 
        aria-label="Scroll ned"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </section>
  );
};

export default Hero;
